/* eslint-disable react/no-unescaped-entities */
import styled from 'styled-components';
import { useRouter } from 'next/router';
import Image from 'next/image';
import Link from 'next/link';

import ButtonLink from '../../components/atoms/button-link/button-link';
import LoadingIcon from '../../components/atoms/loading-icon/loading-icon';
import TabTitle from '../../components/atoms/tab-title/tab-title';
import { MetaPages } from '../../enums/pages';
import SocialMediaShareSection from '../../components/molecules/social-media-share-section/social-media-share-section';
import { getFeaturedFriendsOfTheKitchenPageDetails } from '../../services/inspirations-and-planning';
import { FriendsOfTheKitchenPageDetailsType } from '../../types/content';
import Breadcrumbs from '../../components/atoms/breadcrumbs/breadcrumbs';
import ContainerWithSidebar from '../../components/atoms/container-with-sidebar/container-with-sidebar';
import MarginSpacing from '../../components/atoms/margin-spacing/margin-spacing';
import FeaturesSidebar from '../../components/molecules/features-sidebar/features-sidebar';
import { pageRoute } from '../../config/routes/pages';
import { NewsAndEventsModel } from '../../interface/news-and-events';
import { getFeaturedNewsAndEvents } from '../../services/news';
import Carousel from '../../components/molecules/carousel/carousel';
import { linkRoutes } from '../../config/routes/links';
import { strapiAssetBaseUrl } from '../../config/routes/assets';

const Container = styled.div`
  p {
    font-family: MuseoSans-100;
    font-size: 14px;
    font-weight: 200 !important;
    margin-bottom: 15px;
    text-align: justify;
  }

  em {
    font-size: 12px;
  }

  p strong {
    font-family: MuseoSans-500;
  }

  ul {
    list-style-type: circle;
    padding-left: 40px;
  }

  h1 {
    font-family: ChronicleDisplayLight;
    font-size: 35px;
    font-weight: 300 !important;
    text-transform: uppercase;
    line-height: 2.25rem;
  }

  a {
    text-decoration-line: underline;
  }

  a:hover {
    color: #0081c6;
  }
`;

type IndexProps = {
  featuredFriendsOfTheKitchenPageDetails: FriendsOfTheKitchenPageDetailsType[];
  featuredNewsAndEvents: NewsAndEventsModel[];
  newsAndEventsArticle: NewsAndEventsModel;
};

const links = {
  showroom: 'https://my.matterport.com/show/?m=Boe2AFEuzJe',
  dualRefSystemArticle: 'https://subzero-wolf.com.ph/news-and-events/26659',
  inductionArticle: 'https://subzero-wolf.com.ph/news-and-events/26657',
  bookAnAppointment:
    'https://calendly.com/fgi-kitchen-bookings?utm_source=Instagram&utm_medium=Bio_Link&utm_campaign=Calendly_Appointments&utm_content=SZWF'
};

const carouselImages = [
  `${strapiAssetBaseUrl}/1_85f6a5925e.jpg`,
  `${strapiAssetBaseUrl}/2_3e9299e8bc.jpg`,
  `${strapiAssetBaseUrl}/3_6f85f3dae9.jpg`,
  `${strapiAssetBaseUrl}/4_66e1f79def.jpg`
];

function ArticleContent() {
  return (
    <article className="flex flex-col gap-4">
      <h1>INDULGE IN A FRESH PERSPECTIVE</h1>
      <p>
        Embark on a culinary adventure made with fresh ingredients as Sub-Zero
        and Wolf partners with Good Food Community for a ‘Freshness Medley’
      </p>
      <p>
        July 29, 2023 - In the pursuit of freshness and flavor, Sub-Zero and
        Wolf, in partnership with Good Food Community (GFC), pay tribute to the
        farmers and local communities responsible for cultivating the most
        delicious and nutritious produce imaginable through the{' '}
        <Link href={linkRoutes.SEED_TO_PLATE}>“Seed to Plate” campaign</Link>.
      </p>
      <p>
        Sub-Zero and Wolf’s philosophy rests on the premise that delicious food
        emerges from the freshest of ingredients, hence their commitment to
        upholding this very standard. In doing so, they’ve started from the
        ground up and recognized the dedicated farmers who cultivate everyone’s
        produce with care. Through a captivating culinary experience complete
        with freshness and flavor, the ‘Freshness Medley’ is a reminder of the
        value of food's journey from seed to plate.
      </p>
      <Carousel
        imageURLs={carouselImages}
        name="Summer in the Hamptons article"
      />

      <p>
        Set on a rainy afternoon, guests were treated to a display of recently
        harvested vegetables all around the Sub-Zero and Wolf showroom, located
        at The Residences at Greenbelt, Makati City. Tables were adorned with
        vibrant, natural produce, shining a spotlight on the invigorating
        organic and rich flavors of GFC's harvest. These fresh fruits and
        vegetables were also showcased in the Sub-Zero freshness display
        narrating the heartfelt story of the campaign.
      </p>

      <p>
        Watch the video and get a glimpse of all the excitement at the event
        last July 29.
      </p>

      <iframe
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        className="aspect-video w-full"
        frameBorder="0"
        src={`${strapiAssetBaseUrl}/Cooking_Demo_Reel_1080p_006e2761fd.mp4`}
        title="YouTube video player"
      />

      <p>
        After guests settled into their seats, a video of the brands'{' '}
        <Link href="https://bit.ly/SeedToPlateYTVideo">
          Seed to Plate advocacy
        </Link>{' '}
        was played, captivating attendees with the partnership's mission and
        immersing them into the atmosphere of the event. Then as it concluded,
        all eyes shifted towards GFC’s Operations Lead and Chef, Joyce Santos,
        and Corporate Chef, Alexis Cruz, for their delectable four-course
        presentation highlighting harvest sourced from GFC.
      </p>

      <Image
        alt="Cooking Demo with Audience"
        height={600}
        src={`${strapiAssetBaseUrl}/5_7eb144b38a.jpg`}
        width={960}
      />

      <div>
        <div className="md:float-right mb-6 md:mb-0 md:ml-6">
          <Image
            alt="Cassava Blini with Spinach Gratin"
            className="object-contain object-center"
            height={250}
            src={`${strapiAssetBaseUrl}/6_083b881587.jpg`}
            width={370}
          />
        </div>
        <p className="text-justify">
          The cooking demonstration started off with an enticing introduction,
          featuring the{' '}
          <Link
            href={`${linkRoutes.SEED_TO_PLATE}recipes/cassava-blini-with-spinach-gratin`}
          >
            Cassava Blini with Spinach Gratin
          </Link>{' '}
          as an appetizer. This dish showcased a delicate cassava bite-sized
          pancake crowned with velvety spinach gratin, complemented by the
          addition of munggo tempeh crumble for enhanced texture and flavor. The
          ingenious use of the Wolf Convection Steam Oven (CSO) for this recipe
          fascinated attendees, as they witnessed how it effortlessly steamed
          the cassava to the perfect texture using Steam Mode. The use of the
          perforated pan included in the oven allowed gentle steam to envelope
          the cassava evenly, resulting in a more efficient heat transfer and
          shorter cooking time.
        </p>
      </div>

      <div>
        <div className="md:float-left mb-6 md:mb-0 md:mr-6">
          <Image
            alt="Mixed Grain and Mushroom Bowl with Sesame Crusted Tuna"
            className="object-contain object-center"
            height={270}
            src={`${strapiAssetBaseUrl}/7_3c989e0b35.jpg`}
            width={400}
          />
        </div>
        <p className="text-justify">
          From effortlessly steaming, the convenience offered by the Wolf CSO
          extends further with its ability to allow multi-rack cooking showcased
          through the next dish on the menu -{' '}
          <Link
            href={`${linkRoutes.SEED_TO_PLATE}recipes/mixed-grain-bowl-with-sesame-crusted-tuna`}
          >
            Mixed Grain and Mushroom Bowl with Sesame Crusted Tuna
          </Link>
          . A medley of red rice, adlai, and quinoa was simultaneously cooked in
          the Wolf CSO. Utilizing this versatile appliance not only streamlines
          the cooking process but also conserves valuable time and effort,
          eliminating the need for multiple pots when preparing an assortment of
          grains. Complementing these mixed grains, the umami-packed mushrooms
          and sesame-crusted tuna were seared beautifully in the Wolf Induction
          Cooktop. With its Boost Mode, the chefs were able to achieve rapid
          high heat within seconds. Just as fast as it was cooked, the dish was
          off the pan and on to a plate for everyone to taste. Guests immersed
          themselves in an ensemble that harmoniously blended a variety of
          textures and flavors. The combination of hearty mixed grains, earthy
          mushrooms, and the succulent bite of sesame-crusted tuna created a
          symphony for their palates. To elevate the culinary experience even
          further, a soy-based sauce was introduced, infusing the medley with an
          unforgettable crescendo of tastes.
        </p>
      </div>

      <div className="flex w-full gap-2 justify-center mb-4">
        <Image
          alt="Mahi-Mahi and Bauko Beans Confit"
          className="object-contain"
          height={400}
          src={`${strapiAssetBaseUrl}/8_d68a22b8e4.png`}
          width={500}
        />
        <Image
          alt="Wolf M Series Oven"
          className="object-contain"
          height={400}
          src={`${strapiAssetBaseUrl}/9_a11ca0c276.jpg`}
          width={500}
        />
      </div>

      <p>
        For the second main course, the chefs revealed a harmonious blend of
        flavors through the{' '}
        <Link
          href={`${linkRoutes.SEED_TO_PLATE}recipes/baked-mahi-mahi-and-bauko-beans-confit`}
        >
          Mahi-Mahi and Bauko Beans Confit
        </Link>
        , wielding the help of the Wolf M Series Oven. Equipped with a powerful
        dual convection system, our most advanced oven delivers precise,
        consistent temperature ensuring effortlessly delicious results.
        Full-extension racks also add ease to cooking, making stirring or
        seasoning a breeze. The Mahi-Mahi filets were baked to perfection, as
        guests savored the ocean’s finest, complemented by a luscious confit of
        Bauko beans. To accompany it, the chefs also made velvety Cauliflower
        Mash, prepared by steaming in the Wolf CSO, with a zesty Rosemary
        Sauerkraut fermented naturally and stored in the Sub-Zero Refrigerator.
      </p>

      <div>
        <div className="md:float-right mb-6 md:mb-0 md:ml-6">
          <Image
            alt="Miso Caramel Bulkan Banana Tart"
            className="object-contain object-center"
            height={250}
            src={`${strapiAssetBaseUrl}/10_a67d72cf76.jpg`}
            width={370}
          />
        </div>
        <p className="text-justify">
          Saving the best for last, the dessert impressed guests even further
          with a unique local{' '}
          <Link
            href={`${linkRoutes.SEED_TO_PLATE}recipes/miso-caramel-bulkan-banana-tart`}
          >
            Miso Caramel Bulkan Banana Tart
          </Link>
          . The bananas were coated with a delicious Miso Caramel, crowned with
          a sweet finishing touch of a scoop of ice cream. A true masterpiece
          crafted by the chefs, attendees delighted in how the inherent
          sweetness of the banana harmonized with the savory hints of miso
          caramel. The medley first made its way on the Wolf Induction Cooktop
          where the True Simmer setting was activated for caramelizing sugar
          with flawless precision, then culminating into golden perfection using
          Convection Mode within the confines of the Wolf M Series Oven. The
          culinary stage was further enriched thanks to the team effort of the
          four talented chefs. Their combined skills gave guests the chance to
          enjoy every moment, guaranteeing that each course served was
          impeccably cooked and exquisitely plated.
        </p>
      </div>

      <Image
        alt="Cooking Demo with Audience"
        height={600}
        src={`${strapiAssetBaseUrl}/11_b4dc620c76.jpg`}
        width={960}
      />

      <p>
        With every recipe, the chefs offered compelling insights about the
        unique flavors present in organic ingredients. On top of that, the
        presentation notably displayed Sub-Zero's adeptness in food preservation
        and Wolf's expertise in cooking equipment, elevating the seed-to-plate
        journey to new heights.
      </p>

      <div className="flex w-full justify-center mb-4">
        <Image
          alt="Cooking Demo with Audience"
          className="object-contain"
          height={400}
          src={`${strapiAssetBaseUrl}/12_f59aef06db.png`}
          width={280}
        />
        <Image
          alt="Cooking Demo with Audience"
          className="object-contain"
          height={400}
          src={`${strapiAssetBaseUrl}/13_64380dfae1.png`}
          width={280}
        />
        <Image
          alt="Cooking Demo with Audience"
          className="object-contain"
          height={400}
          src={`${strapiAssetBaseUrl}/14_4ccd7bfeae.png`}
          width={280}
        />
      </div>

      <p>
        Overall, the event narrated a story of how a kitchen can transcend its
        practical purpose to become a place for a unique cooking experience. It
        was truly a demonstration of how exceptional equipment and fresh
        ingredients from dependable farmers can come together to create
        something remarkable in anyone’s kitchen.
      </p>

      <Image
        alt="Cooking Demo with Audience"
        height={600}
        src={`${strapiAssetBaseUrl}/15_f067d00b28.jpg`}
        width={960}
      />

      <p>
        Discover a unique culinary experience of freshness and flavor with
        Sub-Zero and Wolf. Book an{' '}
        <Link href={linkRoutes.CALENDLY}>appointment</Link> with us today to
        learn more.
      </p>
    </article>
  );
}

export function Index(props: IndexProps) {
  const {
    featuredFriendsOfTheKitchenPageDetails,
    featuredNewsAndEvents,
    newsAndEventsArticle
  } = props;

  const router = useRouter();

  if (router.isFallback) {
    return <LoadingIcon />;
  }

  const breadcrumbs = {
    route: newsAndEventsArticle.attributes.slug,
    tabLabel: newsAndEventsArticle.attributes.news_title
  };

  const newsAndEvents = {
    route: pageRoute.NEWS_AND_EVENTS,
    tabLabel: 'NEWS AND EVENTS'
  };

  return (
    <>
      <TabTitle page={MetaPages.NEWS_AND_EVENTS} />
      <ContainerWithSidebar
        featuredContents={
          <FeaturesSidebar
            friendsOfTheKitchenPageDetails={
              featuredFriendsOfTheKitchenPageDetails
            }
            newsAndEventsContent={featuredNewsAndEvents}
          />
        }
      >
        <Breadcrumbs
          color="text-breadcrumbGray"
          subTab={breadcrumbs}
          tab={newsAndEvents}
        />
        <Container>
          <div className="mt-0 sm:mt-10" />
          <ArticleContent />
          <div className="flex justify-end ml-auto mt-10">
            <ButtonLink
              buttonTitle="BACK"
              ctaLink={pageRoute.NEWS_AND_EVENTS}
            />
          </div>
          <MarginSpacing margin="mb-20" />
        </Container>
      </ContainerWithSidebar>
      <MarginSpacing margin="mb-5" />
      <SocialMediaShareSection />
    </>
  );
}

export async function getStaticProps() {
  const featuredFriendsOfTheKitchenPageDetails =
    await getFeaturedFriendsOfTheKitchenPageDetails();
  const featuredNewsAndEvents = await getFeaturedNewsAndEvents();
  const newsAndEventsArticle = {
    attributes: {
      slug: '072923',
      news_title: 'Indulge in a Fresh Perspective'
    }
  } as NewsAndEventsModel;

  return {
    props: {
      featuredFriendsOfTheKitchenPageDetails,
      featuredNewsAndEvents,
      newsAndEventsArticle
    },
    revalidate: 300
  };
}

export default Index;
